/** Routines, as the app has it: the day's review first. Saved routines and templates stay in the app. */

import { Clock } from '@gravity-ui/icons';

import { ArtEmpty, Button, ListBlock, ListGroup, ListRow, StatusMark } from '../../components/ui.tsx';
import { RowMenu } from '../../components/ui/RowMenu.tsx';
import { timeAgo } from '../../hooks.ts';
import type { Review, Workbench } from '../useWorkbench.ts';
import { RemoteView } from './RemoteView.tsx';

const STATUS_TEXT: Record<string, string> = { open: 'Open', accepted: 'Accepted', done: 'Done', dismissed: 'Dismissed' };

export function RoutinesView({ w }: { w: Workbench }) {
  const review = w.useView<{ latest?: Review; running: boolean }>('reviews.latest', undefined, [], 60_000);
  const r = review.data?.latest;
  return (
    <RemoteView narrow>
      <ListGroup
        title="The day's review"
        count={r?.prescriptions.length || undefined}
        description={r ? `${r.date} · generated ${timeAgo(r.generatedAt)}. Prescriptions on spend and habits; accept one to act on it.` : review.data?.running ? 'Running now.' : 'None yet.'}
        actions={
          <Button size="sm" variant="secondary" isPending={review.data?.running} onPress={() => void w.act('Run review', () => w.call('reviews.run', { trigger: 'manual' }), review.reload)}>
            Run now
          </Button>
        }
        empty={
          r ? undefined : (
            <ArtEmpty compact tone="amber" icon={<Clock />} title="No review yet">
              {w.connected ? 'Run one for prescriptions on spend and habits.' : 'The workbench is not connected.'}
            </ArtEmpty>
          )
        }
      >
        {r?.summary ? <ListBlock className="text-sm">{r.summary}</ListBlock> : null}
        {r?.prescriptions.map((p) => (
          <ListRow
            key={p.id}
            title={p.headline}
            description={p.prescription}
            status={<StatusMark state={p.status === 'accepted' || p.status === 'done' ? 'low' : p.status === 'dismissed' ? 'idle' : 'medium'} label={STATUS_TEXT[p.status] ?? p.status} />}
            compactControl
            control={
              p.status !== 'done' && p.status !== 'dismissed' ? (
                <RowMenu
                  label={`More for ${p.headline}`}
                  items={[p.status !== 'accepted' && { id: 'accepted', label: 'Accept' }, { id: 'dismissed', label: 'Dismiss' }]}
                  onAction={(status) => void w.act(status === 'accepted' ? 'Accept' : 'Dismiss', () => w.call('reviews.setStatus', { id: p.id, status }), review.reload)}
                />
              ) : undefined
            }
          />
        ))}
      </ListGroup>
      <p className="m-0 text-xs text-muted">Saved routines and their schedules are managed in the app.</p>
    </RemoteView>
  );
}
